import { useNavigate } from 'react-router-dom';
import { useStudentCourses, useStudentProgression, useCertificates } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  BookOpen, 
  Award,
  Calendar,
  Clock,
  Loader2,
  Download,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const levelColors = {
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const StudentCourses = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { data: courses, isLoading: coursesLoading } = useStudentCourses();
  const { data: progression, isLoading: progressionLoading } = useStudentProgression();
  const { data: certificates } = useCertificates();

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isLoading = coursesLoading || progressionLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-courses-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Mes cours</h1>
        <p className="text-muted-foreground mt-1">
          Suivez votre progression et accédez à vos matériaux
        </p>
      </div>

      {/* Progression Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Niveau actuel
            </CardTitle>
            <TrendingUp className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <Badge className={`${levelColors[progression?.current_level || 'A1']} text-lg px-3 py-1`}>
              {progression?.current_level || 'A1'}
            </Badge>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Cours inscrits
            </CardTitle>
            <BookOpen className="w-5 h-5 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">
              {progression?.enrolled_courses || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Moyenne générale
            </CardTitle>
            <CheckCircle className="w-5 h-5 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">
              {progression?.average_grade !== null ? `${progression.average_grade}%` : '-'}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Certificats
            </CardTitle>
            <Award className="w-5 h-5 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">
              {progression?.certificates_count || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Next Session Card */}
      {progression?.next_session && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-primary/5 to-secondary/5">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Prochaine session</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-lg">{progression.next_session.title}</p>
                <p className="text-muted-foreground">{progression.next_session.course_name}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" />
                  {formatDateTime(progression.next_session.date)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Courses Grid */}
      <div>
        <h2 className="font-heading text-xl font-semibold mb-4">Mes formations</h2>
        
        {courses?.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BookOpen className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Vous n'êtes inscrit à aucun cours</p>
              <p className="text-sm text-muted-foreground mt-1">
                Contactez l'administration pour vous inscrire
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses?.map((course) => (
              <Card 
                key={course.id} 
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/student/courses/${course.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="font-heading text-lg line-clamp-1">
                      {course.name}
                    </CardTitle>
                    <Badge className={levelColors[course.level]}>{course.level}</Badge>
                  </div>
                  {course.description && (
                    <CardDescription className="line-clamp-2">
                      {course.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Teacher */}
                  {course.teacher_name && (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="bg-secondary text-white text-xs">
                          {course.teacher_name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-muted-foreground">{course.teacher_name}</span>
                    </div>
                  )}

                  {/* Next Session */}
                  {course.next_session && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-2 rounded-md">
                      <Calendar className="w-4 h-4" />
                      <span>Prochaine: {formatDateTime(course.next_session.date)}</span>
                    </div>
                  )}

                  {/* Attendance */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Présence</span>
                      <span className="font-medium">{course.attendance_rate}%</span>
                    </div>
                    <Progress value={course.attendance_rate} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Certificates Section */}
      {certificates && certificates.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-semibold mb-4">Mes certificats</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map((cert) => (
              <Card key={cert.id} className="border-0 shadow-sm">
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Niveau {cert.level}</p>
                      <p className="text-xs text-muted-foreground">{cert.course_name}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${cert.pdf_url}`} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCourses;
